import React, { useState, useEffect } from 'react';
import specialitiesService from '../services/specialitiesService';
import './Specialities.css';

// Icon Components
const PlusIcon = () => (
  <svg width="18px" height="18px" strokeWidth="1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor">
    <path d="M6 12H12M18 12H12M12 12V6M12 12V18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
  </svg>
);

const EditIcon = () => (
  <svg width="18px" height="18px" strokeWidth="1.4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor">
    <path d="M3 21L12 21H21" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
    <path d="M12.2218 5.82839L15.0503 2.99996L20 7.94971L17.1716 10.7781M12.2218 5.82839L6.61522 11.435C6.42769 11.6225 6.32233 11.8769 6.32233 12.1421L6.32233 16.6776L10.8579 16.6776C11.1231 16.6776 11.3774 16.5723 11.565 16.3847L17.1716 10.7781M12.2218 5.82839L17.1716 10.7781" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
  </svg>
);

const DeleteIcon = () => (
  <svg width="18px" height="18px" strokeWidth="1.4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor">
    <path d="M8.99219 13H11.9922H14.9922" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
    <path d="M3.03919 4.2939C3.01449 4.10866 3.0791 3.92338 3.23133 3.81499C3.9272 3.31953 6.3142 2 12 2C17.6858 2 20.0728 3.31952 20.7687 3.81499C20.9209 3.92338 20.9855 4.10866 20.9608 4.2939L19.2616 17.0378C19.0968 18.2744 18.3644 19.3632 17.2813 19.9821L16.9614 20.1649C13.8871 21.9217 10.1129 21.9217 7.03861 20.1649L6.71873 19.9821C5.6356 19.3632 4.90325 18.2744 4.73838 17.0378L3.03919 4.2939Z" stroke="currentColor" strokeWidth="1.4"></path>
    <path d="M3 5C5.57143 7.66666 18.4286 7.66662 21 5" stroke="currentColor" strokeWidth="1.4"></path>
  </svg>
);

const Specialities = () => {
  const [specialities, setSpecialities] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectAll, setSelectAll] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Drawer states
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('add'); // 'add' or 'edit'
  const [currentEditId, setCurrentEditId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    status: 'active'
  });

  // Delete modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page on search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch specialities from API
  useEffect(() => {
    fetchSpecialities();
  }, [currentPage, itemsPerPage, filterStatus, debouncedSearchTerm]);

  const fetchSpecialities = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearchTerm,
        status: filterStatus
      };

      const response = await specialitiesService.getAll(params);

      setSpecialities(response.data.map(spec => ({ ...spec, selected: false })));
      setTotalPages(response.pagination.totalPages);
      setTotalItems(response.pagination.total);
      setCurrentPage(response.pagination.page);

    } catch (err) {
      console.error('Error fetching specialities:', err);
      setError('Failed to load specialities. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const speciality = specialities.find(spec => spec.id === id);
      const newStatus = speciality.status === 'active' ? 'inactive' : 'active';

      await specialitiesService.update(id, {
        name: speciality.name,
        status: newStatus
      });

      // Update local state
      setSpecialities(specialities.map(spec =>
        spec.id === id ? { ...spec, status: newStatus } : spec
      ));

    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status. Please try again.');
    }
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setSpecialities(specialities.map(spec => ({ ...spec, selected: newSelectAll })));
  };

  const handleSelectOne = (id) => {
    setSpecialities(specialities.map(spec =>
      spec.id === id ? { ...spec, selected: !spec.selected } : spec
    ));
  };

  const handleEdit = (id) => {
    const speciality = specialities.find(spec => spec.id === id);
    if (speciality) {
      setFormData({
        name: speciality.name,
        status: speciality.status
      });
      setCurrentEditId(id);
      setDrawerMode('edit');
      setIsDrawerOpen(true);
    }
  };

  const handleDelete = (id) => {
    setDeleteTargetId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await specialitiesService.delete(deleteTargetId);

      // Update local state
      setSpecialities(specialities.filter(spec => spec.id !== deleteTargetId));
      setIsDeleteModalOpen(false);
      setDeleteTargetId(null);

    } catch (err) {
      console.error('Error deleting speciality:', err);
      alert('Failed to delete speciality. Please try again.');
      setIsDeleteModalOpen(false);
      setDeleteTargetId(null);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setDeleteTargetId(null);
  };

  const handleAddNew = () => {
    setFormData({ name: '', status: 'active' });
    setDrawerMode('add');
    setCurrentEditId(null);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setFormData({ name: '', status: 'active' });
    setCurrentEditId(null);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    try {
      if (drawerMode === 'add') {
        const response = await specialitiesService.create({
          name: formData.name,
          status: formData.status
        });

        // Add to local state
        setSpecialities([{ ...response.data, selected: false }, ...specialities]);
        setTotalItems(totalItems + 1);

      } else {
        const response = await specialitiesService.update(currentEditId, {
          name: formData.name,
          status: formData.status
        });

        // Update local state
        setSpecialities(specialities.map(spec =>
          spec.id === currentEditId ? { ...response.data, selected: false } : spec
        ));
      }

      handleCloseDrawer();

    } catch (err) {
      console.error('Error saving speciality:', err);
      if (err.response?.data?.message) {
        alert(err.response.data.message);
      } else {
        alert('Failed to save speciality. Please try again.');
      }
    }
  };

  const handleFormStatusToggle = () => {
    setFormData({
      ...formData,
      status: formData.status === 'active' ? 'inactive' : 'active'
    });
  };

  return (
    <div className="specialities-container">
      <div className="specialities-header">
        <div className="header-left">
          <h1 className="page-title">Specialities</h1>
          <p className="page-description">Manage medical specialities for the platform</p>
        </div>
        <div className="header-right">
          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <button className="add-btn" onClick={handleAddNew}>
            <PlusIcon />
            <span>Add Speciality</span>
          </button>
        </div>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search specialities..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="table-container">
        <table className="specialities-table">
          <thead>
            <tr>
              <th className="checkbox-col">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  className="checkbox"
                />
              </th>
              <th className="speciality-col">Speciality</th>
              <th className="status-col">Status</th>
              <th className="actions-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" className="loading-state">
                  Loading specialities...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="4" className="error-state">
                  {error}
                </td>
              </tr>
            ) : specialities.length > 0 ? (
              specialities.map((speciality) => (
                <tr key={speciality.id}>
                  <td className="checkbox-col">
                    <input
                      type="checkbox"
                      checked={speciality.selected}
                      onChange={() => handleSelectOne(speciality.id)}
                      className="checkbox"
                    />
                  </td>
                  <td className="speciality-col">
                    <span className="speciality-name">{speciality.name}</span>
                  </td>
                  <td className="status-col">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={speciality.status === 'active'}
                        onChange={() => handleToggleStatus(speciality.id)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <span className={`status-badge ${speciality.status}`}>
                      {speciality.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="actions-col">
                    <div className="action-buttons">
                      <button
                        className="action-btn edit-btn"
                        onClick={() => handleEdit(speciality.id)}
                        title="Edit"
                      >
                        <EditIcon />
                      </button>
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(speciality.id)}
                        title="Delete"
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="no-data">
                  No specialities found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && !error && totalItems > 0 && (
        <div className="pagination">
          <div className="pagination-left">
            <span className="pagination-label">Show</span>
            <select
              className="pagination-select"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="pagination-label">entries</span>
          </div>
          <div className="pagination-center">
            <span className="pagination-info">
              Page {currentPage} of {totalPages} ({totalItems} total)
            </span>
          </div>
          <div className="pagination-right">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Drawer */}
      <div className={`drawer-overlay ${isDrawerOpen ? 'open' : ''}`} onClick={handleCloseDrawer}></div>
      <div className={`drawer ${isDrawerOpen ? 'open' : ''}`}>
        <div className="drawer-header">
          <h2 className="drawer-title">
            {drawerMode === 'add' ? 'Add New Speciality' : 'Edit Speciality'}
          </h2>
          <button className="drawer-close" onClick={handleCloseDrawer}>
            <svg width="24px" height="24px" strokeWidth="1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor">
              <path d="M6 18L18 6M6 6L18 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
            </svg>
          </button>
        </div>

        <form onSubmit={handleFormSubmit} className="drawer-form">
          <div className="form-group">
            <label htmlFor="name" className="form-label">Speciality Name</label>
            <input
              type="text"
              id="name"
              className="form-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter speciality name"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Status</label>
            <div className="status-toggle-group">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={formData.status === 'active'}
                  onChange={handleFormStatusToggle}
                />
                <span className="toggle-slider"></span>
              </label>
              <span className={`status-badge ${formData.status}`}>
                {formData.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          <div className="drawer-actions">
            <button type="button" className="btn-cancel" onClick={handleCloseDrawer}>
              Cancel
            </button>
            <button type="submit" className="btn-submit">
              {drawerMode === 'add' ? 'Add Speciality' : 'Update Speciality'}
            </button>
          </div>
        </form>
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <>
          <div className="modal-overlay" onClick={handleCancelDelete}></div>
          <div className="delete-modal">
            <div className="modal-icon">
              <svg width="48px" height="48px" strokeWidth="1.4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="#F07167">
                <path d="M8.99219 13H11.9922H14.9922" stroke="#F07167" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
                <path d="M3.03919 4.2939C3.01449 4.10866 3.0791 3.92338 3.23133 3.81499C3.9272 3.31953 6.3142 2 12 2C17.6858 2 20.0728 3.31952 20.7687 3.81499C20.9209 3.92338 20.9855 4.10866 20.9608 4.2939L19.2616 17.0378C19.0968 18.2744 18.3644 19.3632 17.2813 19.9821L16.9614 20.1649C13.8871 21.9217 10.1129 21.9217 7.03861 20.1649L6.71873 19.9821C5.6356 19.3632 4.90325 18.2744 4.73838 17.0378L3.03919 4.2939Z" stroke="#F07167" strokeWidth="1.4"></path>
                <path d="M3 5C5.57143 7.66666 18.4286 7.66662 21 5" stroke="#F07167" strokeWidth="1.4"></path>
              </svg>
            </div>
            <h3 className="modal-title">Delete Speciality</h3>
            <p className="modal-message">
              Are you sure you want to delete "{specialities.find(s => s.id === deleteTargetId)?.name}"? This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button className="modal-btn modal-btn-cancel" onClick={handleCancelDelete}>
                Cancel
              </button>
              <button className="modal-btn modal-btn-delete" onClick={handleConfirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Specialities;
